import { useMemo, useState } from 'react'
import { useNavigate } from '@tanstack/react-router'
import type { ColumnDef } from '@tanstack/react-table'
import { toast } from 'sonner'
import { ArrowLeft } from 'lucide-react'
import {
  Button,
  Chip,
  DataTable,
  EmptyState,
  MetricCard,
  MetricStrip,
  PageHeaderBand,
  QueryErrorState,
  Select,
} from '@/components/ui'
import { useUnscheduleUpcoming, useUpcomingPriceChanges } from '../api/queries'
import type { UpcomingPriceChange } from '../api/types'
import { daysUntil, formatIsoDate, kpiCounts } from '../utils/upcoming'

function money(v: number | null, unit: string | null): string {
  if (v == null) return '—'
  return `$${v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}${unit ? ` /${unit}` : ''}`
}

/**
 * Vendor price changes that are approved but not yet in Odoo because the
 * vendor's sheet has a future effective date. Purchasing keeps today's price
 * until the date; this is where the team sees what is coming.
 */
export function UpcomingPriceChangesPage() {
  const navigate = useNavigate()
  const { data, isLoading, isError, error, refetch } = useUpcomingPriceChanges()
  const unschedule = useUnscheduleUpcoming()
  const [vendor, setVendor] = useState('')

  const all = useMemo<UpcomingPriceChange[]>(() => data?.changes ?? [], [data])
  const vendors = useMemo(
    () => [...new Set(all.map((c) => c.vendor_name ?? `Vendor ${c.vendor_id}`))].sort(),
    [all],
  )
  const rows = useMemo(
    () => all.filter((c) => !vendor || (c.vendor_name ?? `Vendor ${c.vendor_id}`) === vendor),
    [all, vendor],
  )
  const kpi = useMemo(() => kpiCounts(all), [all])

  function doUnschedule(c: UpcomingPriceChange) {
    const label = c.product_name ?? `line ${c.line_id}`
    if (!window.confirm(`Unschedule ${label}? It goes back to review and Odoo will not change.`)) return
    unschedule.mutate(
      { intakeId: c.intake_id, lineIds: [c.line_id] },
      {
        onSuccess: () => toast.success(`${label} unscheduled.`),
        onError: (err) => toast.error(err instanceof Error ? err.message : 'Unschedule failed.'),
      },
    )
  }

  const columns = useMemo<ColumnDef<UpcomingPriceChange, unknown>[]>(
    () => [
      {
        id: 'effective_date',
        header: 'Effective',
        accessorKey: 'effective_date',
        cell: ({ row: { original: c } }) => {
          const d = daysUntil(c.effective_date)
          return (
            <span className="flex items-center gap-2">
              {formatIsoDate(c.effective_date)}
              {c.overdue ? (
                <Chip size="xs" variant="danger">Failed to apply</Chip>
              ) : (
                <span className="text-xs text-tertiary">{d === 0 ? 'today' : `in ${d}d`}</span>
              )}
            </span>
          )
        },
      },
      { id: 'vendor_name', header: 'Vendor', accessorFn: (c) => c.vendor_name ?? `Vendor ${c.vendor_id}` },
      { id: 'product_name', header: 'Product', accessorKey: 'product_name' },
      {
        id: 'min_qty',
        header: 'Min qty',
        accessorKey: 'min_qty',
        cell: (ctx) => (ctx.getValue() as number).toLocaleString(),
      },
      {
        id: 'current_price',
        header: 'Current cost',
        accessorKey: 'current_price',
        cell: ({ row: { original: c } }) => money(c.current_price, c.odoo_uom),
      },
      {
        id: 'apply_price',
        header: 'New cost',
        accessorKey: 'apply_price',
        cell: ({ row: { original: c } }) => money(c.apply_price, c.odoo_uom),
      },
      {
        id: 'pct_change',
        header: 'Change',
        accessorKey: 'pct_change',
        cell: ({ row: { original: c } }) =>
          c.pct_change == null ? (
            '—'
          ) : (
            <Chip size="xs" variant={c.pct_change > 0 ? 'warning' : 'success'}>
              {c.pct_change > 0 ? '+' : ''}
              {c.pct_change.toFixed(1)}%
            </Chip>
          ),
      },
      {
        id: 'scheduled_by',
        header: 'Scheduled by',
        accessorFn: (c) => c.scheduled_by ?? '—',
      },
      {
        id: 'actions',
        header: '',
        enableSorting: false,
        cell: ({ row: { original: c } }) => (
          <span className="flex gap-1" onClick={(e) => e.stopPropagation()}>
            <Button
              variant="ghost"
              size="sm"
              onClick={() =>
                navigate({
                  to: '/vendor-pricelists/$vendorId/imports/$intakeId',
                  params: { vendorId: String(c.vendor_id), intakeId: c.intake_id },
                })
              }
            >
              Sheet
            </Button>
            <Button variant="ghost" size="sm" disabled={unschedule.isPending} onClick={() => doUnschedule(c)}>
              Unschedule
            </Button>
          </span>
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [navigate, unschedule.isPending],
  )

  return (
    <div data-density="compact" className="flex h-full flex-col">
      <PageHeaderBand
        title="Upcoming vendor price changes"
        description="Approved on a vendor sheet, effective on a later date. Odoo keeps today's cost until then."
      >
        <Button variant="ghost" size="sm" onClick={() => navigate({ to: '/vendor-price-changes' })}>
          <ArrowLeft size={12} /> Back to queue
        </Button>
        <MetricStrip variant="inline">
          <MetricCard variant="inline" label="Next 30 days" value={kpi.d30.toLocaleString()} />
          <MetricCard variant="inline" label="Next 60 days" value={kpi.d60.toLocaleString()} />
          <MetricCard variant="inline" label="Next 90 days" value={kpi.d90.toLocaleString()} />
          <MetricCard
            variant="inline"
            label="Failed to apply"
            value={kpi.overdue.toLocaleString()}
            valueTone={kpi.overdue > 0 ? 'danger' : 'default'}
          />
        </MetricStrip>
      </PageHeaderBand>

      <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-auto bg-canvas p-4">
        <div className="flex items-center gap-2">
          <Select
            aria-label="Vendor"
            value={vendor}
            onChange={(e) => setVendor(e.target.value)}
            className="w-56"
          >
            <option value="">All vendors</option>
            {vendors.map((v) => (
              <option key={v} value={v}>
                {v}
              </option>
            ))}
          </Select>
          <span className="text-xs text-tertiary">{rows.length} line(s)</span>
        </div>
        {isError ? (
          <QueryErrorState error={error} onRetry={() => void refetch()} />
        ) : (
          <DataTable
            data={rows}
            columns={columns}
            loading={isLoading}
            getRowId={(r) => String(r.line_id)}
            moduleId="vendor-price-changes-upcoming"
            maxHeight="100%"
            minTableWidth={1100}
            emptyState={
              <EmptyState
                variant="no-data"
                title="Nothing scheduled"
                description="When a vendor sheet is applied with a future effective date, its lines show up here."
              />
            }
          />
        )}
      </div>
    </div>
  )
}
